import { AlertDialog as BaseAlertDialog } from "@base-ui/react/alert-dialog";
import { useRef, type ReactElement, type ReactNode } from "react";
import { Button } from "../Button/Button";
import styles from "../Dialog/Dialog.module.css";

export interface AlertDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Names the object and action: "Delete lot T030-24S?" */
  title: ReactNode;
  /** The consequence: "Its 12 stock movements are kept in the audit log; the lot disappears from stock." */
  description: ReactNode;
  /** Repeats the verb and object: "Delete lot T030-24S". */
  confirmLabel: string;
  cancelLabel?: string;
  onConfirm: () => void;
  /** Keeps the dialog open with a spinner on the confirm button while the action runs. */
  pending?: boolean;
  tone?: "danger" | "neutral";
  trigger?: ReactElement;
  children?: ReactNode;
}

/**
 * Confirmation for irreversible or wide-impact actions (Base UI AlertDialog). Cancel gets the
 * initial focus. Reversible actions should act at once and offer Undo in a toast instead.
 */
export function AlertDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  pending = false,
  tone = "danger",
  trigger,
  children,
}: AlertDialogProps) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  return (
    <BaseAlertDialog.Root open={open} onOpenChange={onOpenChange ? (next) => onOpenChange(next) : undefined}>
      {trigger ? <BaseAlertDialog.Trigger render={trigger} /> : null}
      <BaseAlertDialog.Portal>
        <BaseAlertDialog.Backdrop className={styles.backdrop} />
        <BaseAlertDialog.Viewport className={styles.viewport}>
          <BaseAlertDialog.Popup className={styles.popup} data-size="sm" initialFocus={cancelRef}>
            <header className={styles.header}>
              <div className={styles.heading}>
                <BaseAlertDialog.Title className={styles.title}>{title}</BaseAlertDialog.Title>
                <BaseAlertDialog.Description className={styles.description}>{description}</BaseAlertDialog.Description>
              </div>
            </header>
            {children ? <div className={styles.body}>{children}</div> : null}
            <footer className={styles.footer}>
              <BaseAlertDialog.Close render={<Button ref={cancelRef} variant="secondary" disabled={pending} />}>
                {cancelLabel}
              </BaseAlertDialog.Close>
              <Button variant={tone === "danger" ? "danger" : "primary"} pending={pending} onClick={onConfirm}>
                {confirmLabel}
              </Button>
            </footer>
          </BaseAlertDialog.Popup>
        </BaseAlertDialog.Viewport>
      </BaseAlertDialog.Portal>
    </BaseAlertDialog.Root>
  );
}
